import type { ComponentProps } from 'react'
import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-transparent text-sm font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-primary/90',
                outline: 'border-border bg-card text-foreground hover:bg-muted',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
                ghost: 'text-foreground hover:bg-muted',
                destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
                link: 'h-auto px-0 text-primary underline-offset-4 hover:underline'
            },
            size: {
                default: 'h-10 px-4',
                sm: 'h-9 px-3',
                lg: 'h-11 px-5 text-[0.9375rem]',
                icon: 'size-10',
                'icon-sm': 'size-9'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

type ButtonProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants>

function Button({
    className,
    variant,
    size,
    type = 'button',
    ...props
}: ButtonProps) {
    return (
        <button
            type={type}
            data-slot='button'
            className={cn(buttonVariants({ variant, size }), className)}
            {...props}
        />
    )
}

export { Button, buttonVariants }
